"use client";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { TurnstileWidget } from "@/components/public/TurnstileWidget";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, MessageSquare, Phone, Send } from "lucide-react";
import { useState, useRef } from "react";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { reportClientError } from "@/lib/observability/report-client-error";

type ContactFormState = {
  name: string;
  email: string;
  category: string;
  subject: string;
  message: string;
  supportReference: string; // Honeypot
};

const defaultState: ContactFormState = {
  name: "",
  email: "",
  category: "general",
  subject: "",
  message: "",
  supportReference: "",
};

const DISCORD_INVITE_URL = "https://discord.gg/vidseedsai";
const DISCORD_INVITE_LABEL = "discord.gg/vidseedsai";

export default function ContactPage() {
  const { t } = useJsonTranslation("ContactPage");
  const turnstileSiteKey =
    process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY ?? "";
  const [form, setForm] = useState<ContactFormState>(defaultState);
  const formStartedAtRef = useRef<number>(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<
    "idle" | "success" | "error" | "turnstile-pending"
  >("idle");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileRenderKey, setTurnstileRenderKey] = useState(0);
  const turnstileRequired = Boolean(turnstileSiteKey);
  const canSubmit =
    !submitting && (!turnstileRequired || Boolean(turnstileToken));

  const handleChange = (key: keyof ContactFormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setStatus("idle");

    try {
      if (turnstileRequired && !turnstileToken) {
        setStatus("turnstile-pending");
        setSubmitting(false);
        return;
      }

      const response = await fetch("/api/support/contact/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          category: form.category,
          subject: form.subject || t("form.defaultSubject"),
          message: form.message,
          supportReference: form.supportReference,
          formStartedAt: formStartedAtRef.current,
          source: "contact",
          turnstileToken,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      setForm(defaultState);
      formStartedAtRef.current = Date.now();
      setStatus("success");
    } catch (error) {
      reportClientError(error, {
        source: "pages.contact",
        feature: "contact",
        action: "handleSubmit",
      });
      setStatus("error");
    } finally {
      setSubmitting(false);
      if (turnstileSiteKey) {
        setTurnstileToken(null);
        setTurnstileRenderKey((current) => current + 1);
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 pt-24 sm:py-16">
      {/* Header */}
      <section className="mx-auto mb-12 max-w-3xl text-center">
        <h1 className="text-foreground text-wrap-balance mb-3 text-4xl font-bold sm:text-5xl">
          {t("hero.title")}
        </h1>
        <p className="text-muted-foreground text-lg">{t("hero.subtitle")}</p>
      </section>

      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        {/* Contact Form */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle>{t("form.cardTitle")}</CardTitle>
            <CardDescription>{t("form.cardDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-5"
              onSubmit={handleSubmit}
              autoComplete="on"
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="name">{t("form.fields.name.label")}</Label>
                  <Input
                    id="name"
                    name="name"
                    autoComplete="name"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder={t("form.fields.name.placeholder")}
                    className="border-border bg-foreground/5"
                  />
                </div>
                <div>
                  <Label htmlFor="email">{t("form.fields.email.label")}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder={t("form.fields.email.placeholder")}
                    className="border-border bg-foreground/5"
                  />
                </div>
              </div>

              {/* Honeypot field */}
              <div
                aria-hidden="true"
                className="absolute -left-[10000px] h-px w-px overflow-hidden"
              >
                <Input
                  id="supportReference"
                  name="supportReference"
                  value={form.supportReference}
                  onChange={(e) =>
                    handleChange("supportReference", e.target.value)
                  }
                  tabIndex={-1}
                  aria-hidden="true"
                  autoComplete="off"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="category">
                    {t("form.fields.category.label")}
                  </Label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => handleChange("category", v)}
                  >
                    <SelectTrigger className="border-border bg-foreground/5">
                      <SelectValue
                        placeholder={t("form.fields.category.placeholder")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">
                        {t("form.fields.category.options.general")}
                      </SelectItem>
                      <SelectItem value="technical">
                        {t("form.fields.category.options.technical")}
                      </SelectItem>
                      <SelectItem value="billing">
                        {t("form.fields.category.options.billing")}
                      </SelectItem>
                      <SelectItem value="partnership">
                        {t("form.fields.category.options.partnership")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="subject">
                    {t("form.fields.subject.label")}
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={form.subject}
                    onChange={(e) => handleChange("subject", e.target.value)}
                    placeholder={t("form.fields.subject.placeholder")}
                    className="border-border bg-foreground/5"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message">
                  {t("form.fields.message.label")}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  placeholder={t("form.fields.message.placeholder")}
                  className="border-border bg-foreground/5"
                />
              </div>

              {turnstileSiteKey ? (
                <TurnstileWidget
                  key={turnstileRenderKey}
                  siteKey={turnstileSiteKey}
                  onTokenChange={setTurnstileToken}
                />
              ) : null}

              {status === "success" && (
                <Alert className="bg-leaf-500/10 border-leaf-500/30">
                  <AlertTitle className="text-leaf-400">
                    {t("status.success.title")}
                  </AlertTitle>
                  <AlertDescription className="text-muted-foreground">
                    {t("status.success.description")}
                  </AlertDescription>
                </Alert>
              )}

              {status === "error" && (
                <Alert variant="destructive">
                  <AlertTitle>{t("status.error.title")}</AlertTitle>
                  <AlertDescription>
                    {t("status.error.description")}
                  </AlertDescription>
                </Alert>
              )}

              {status === "turnstile-pending" && (
                <Alert className="border-amber-500/30 bg-amber-500/10">
                  <AlertTitle className="text-amber-400">
                    {t("status.turnstilePending.title")}
                  </AlertTitle>
                  <AlertDescription className="text-muted-foreground">
                    {t("status.turnstilePending.description")}
                  </AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="bg-leaf-500 hover:bg-leaf-600 w-full"
                disabled={!canSubmit}
              >
                {submitting ? (
                  t("form.sending")
                ) : (
                  <>
                    {t("form.submit")} <Send className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="space-y-6">
          <Card variant="glass">
            <CardHeader>
              <CardTitle>{t("info.title")}</CardTitle>
              <CardDescription>{t("info.description")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Mail className="text-leaf-500 mt-1 h-5 w-5" />
                <div>
                  <p className="text-muted-foreground text-sm">
                    {t("info.emailLabel")}
                  </p>
                  <a
                    href="mailto:support@vidseeds.ai"
                    className="hover:text-leaf-600 dark:hover:text-leaf-400 text-foreground font-medium transition-colors"
                  >
                    support@vidseeds.ai
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MessageSquare className="text-leaf-500 mt-1 h-5 w-5" />
                <div>
                  <p className="text-muted-foreground text-sm">
                    {t("info.discordLabel")}
                  </p>
                  <a
                    href={DISCORD_INVITE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-leaf-600 dark:hover:text-leaf-400 text-foreground font-medium transition-colors"
                  >
                    {DISCORD_INVITE_LABEL}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="text-leaf-500 mt-1 h-5 w-5" />
                <div>
                  <p className="text-muted-foreground text-sm">
                    {t("info.responseTimeLabel")}
                  </p>
                  <p className="text-foreground font-medium">
                    {t("info.responseTimeValue")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card variant="glass">
            <CardHeader>
              <CardTitle>{t("officeHours.title")}</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-2">
              <p>{t("officeHours.weekdays")}</p>
              <p>{t("officeHours.weekends")}</p>
              <p className="text-muted-foreground mt-4 text-sm">
                {t("officeHours.note")}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
